import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { cn } from "../../lib/utils";

type DatePickerProps = {
  value: string;
  min?: string;
  onChange: (value: string) => void;
  placeholder?: string;
};

const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function parseDate(str: string): Date | null {
  if (!str) return null;
  const [y, m, d] = str.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function toValue(date: Date): string {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatDisplay(str: string): string {
  const d = parseDate(str);
  if (!d) return "";
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const DatePicker: React.FC<DatePickerProps> = ({ value, min, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(() => parseDate(value), [value]);
  const minDate = useMemo(() => parseDate(min || ""), [min]);

  const [viewMonth, setViewMonth] = useState(() => {
    const ref = selected || minDate || new Date();
    return { year: ref.getFullYear(), month: ref.getMonth() };
  });

  useEffect(() => {
    if (!open) return;
    const ref = selected || minDate || new Date();
    setViewMonth({ year: ref.getFullYear(), month: ref.getMonth() });
  }, [open, selected, minDate]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const navigate = useCallback((dir: -1 | 1) => {
    setViewMonth((prev) => {
      let m = prev.month + dir;
      let y = prev.year;
      if (m < 0) { m = 11; y -= 1; }
      if (m > 11) { m = 0; y += 1; }
      return { year: y, month: m };
    });
  }, []);

  const days = useMemo(() => {
    const firstDay = new Date(viewMonth.year, viewMonth.month, 1).getDay();
    const daysInMonth = new Date(viewMonth.year, viewMonth.month + 1, 0).getDate();
    const cells: Array<{ date: Date; disabled: boolean } | null> = [];

    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(viewMonth.year, viewMonth.month, d);
      const disabled = minDate ? date < minDate && !isSameDay(date, minDate) : false;
      cells.push({ date, disabled });
    }
    return cells;
  }, [viewMonth, minDate]);

  const handleSelect = (date: Date) => {
    onChange(toValue(date));
    setOpen(false);
  };

  const today = useMemo(() => new Date(), []);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className={cn(
          "w-full px-5 py-4 bg-white border-2 border-dashed border-gray-200 rounded-2xl text-sm font-black text-center transition-all hover:border-gray-300 focus:outline-none focus:border-green-500/30",
          value ? "text-gray-900" : "text-gray-400",
        )}
      >
        {value ? formatDisplay(value) : (placeholder || "Select a date")}
      </button>

      {open && (
        <div className="absolute z-50 mt-2 left-1/2 -translate-x-1/2 w-[320px] bg-white rounded-2xl shadow-xl border border-gray-100 p-4 animate-in fade-in zoom-in-95 duration-150">
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
            >
              <FiChevronLeft className="text-gray-600" />
            </button>
            <span className="text-sm font-black text-gray-900 tracking-tight">
              {MONTHS[viewMonth.month]} {viewMonth.year}
            </span>
            <button
              type="button"
              onClick={() => navigate(1)}
              className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
            >
              <FiChevronRight className="text-gray-600" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {DAYS.map((d) => (
              <div
                key={d}
                className="text-center text-[10px] font-bold text-gray-400 uppercase tracking-widest py-1"
              >
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-0.5">
            {days.map((cell, i) => {
              if (!cell) {
                return <div key={`empty-${i}`} className="aspect-square" />;
              }

              const isSelected = selected && isSameDay(cell.date, selected);
              const isToday = isSameDay(cell.date, today);

              return (
                <button
                  key={cell.date.getTime()}
                  type="button"
                  disabled={cell.disabled}
                  onClick={() => handleSelect(cell.date)}
                  className={cn(
                    "aspect-square flex items-center justify-center rounded-xl text-xs font-bold transition-all",
                    cell.disabled && "text-gray-200 cursor-not-allowed",
                    !cell.disabled && !isSelected && "text-gray-700 hover:bg-green-50 hover:text-green-700",
                    isSelected && "bg-[#00A651] text-white shadow-md shadow-green-200",
                    !isSelected && isToday && !cell.disabled && "ring-1 ring-inset ring-green-300 text-green-700",
                  )}
                >
                  {cell.date.getDate()}
                </button>
              );
            })}
          </div>

          {selected && (
            <div className="mt-3 pt-3 border-t border-gray-100 text-center">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                {formatDisplay(value)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DatePicker;
